// crewbit-admin/src/components/money/ActionRequiredBanner.tsx
//
// SPEC §Overview.7.2. Generic banner surfacing items that require operator
// attention. v1 callers pass at most one item: `disputes_open` when
// `summary.disputes_open.count > 0`. Forward-compat for future kinds (KYC
// pending, payout failed, receipt review).
//
// Calm-empty: returns null when items.length === 0. Absence is the signal.
//
// Tone: amber (urgent attention) — matches the tone used by mobile
// DisputesBanner. Disputes can become red if a SLA threshold is added later.

import { AlertTriangle, ChevronRight } from 'lucide-react';
import { formatMoney } from '../../lib/money';
import { palette, radii, spacing, transition } from '../../styles/tokens';

export type ActionRequiredItem =
  | { kind: 'disputes_open'; count: number; amount: number }
  | { kind: 'kyc_pending' }
  | { kind: 'payout_failed'; count: number };

interface ActionRequiredBannerProps {
  items: ActionRequiredItem[];
  onItemTap?: (item: ActionRequiredItem) => void;
}

interface ItemView {
  label: string;
  trailing: string | null;
}

function viewFor(item: ActionRequiredItem): ItemView {
  switch (item.kind) {
    case 'disputes_open': {
      const noun = item.count === 1 ? 'dispute' : 'disputes';
      return {
        label: `${item.count} open ${noun}`,
        trailing: item.amount > 0 ? formatMoney(item.amount) : null,
      };
    }
    case 'kyc_pending':
      return {
        label: 'KYC verification pending',
        trailing: null,
      };
    case 'payout_failed': {
      const noun = item.count === 1 ? 'payout' : 'payouts';
      return {
        label: `${item.count} failed ${noun}`,
        trailing: null,
      };
    }
  }
}

interface RowProps {
  item: ActionRequiredItem;
  view: ItemView;
  onTap?: () => void;
  isLast: boolean;
}

function Row({ view, onTap, isLast }: RowProps) {
  const interactive = Boolean(onTap);
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={!interactive}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        width: '100%',
        padding: '10px 12px',
        border: 'none',
        background: 'transparent',
        cursor: interactive ? 'pointer' : 'default',
        textAlign: 'left',
        fontFamily: 'inherit',
        borderBottom: isLast ? 'none' : `1px solid ${palette.amberWash}`,
        transition: `background ${transition.fast}`,
      }}
    >
      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: palette.amber }}>
        {view.label}
      </span>
      {view.trailing && (
        <span style={{
          fontSize: 13,
          fontWeight: 600,
          color: palette.amber,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {view.trailing}
        </span>
      )}
      {interactive && <ChevronRight size={14} color={palette.amber} />}
    </button>
  );
}

export function ActionRequiredBanner({ items, onItemTap }: ActionRequiredBannerProps) {
  if (items.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Action required"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: palette.amberWash,
        border: `1px solid ${palette.amber}`,
        borderRadius: radii.md,
        marginBottom: spacing.lg,
        overflow: 'hidden',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xs,
        padding: '8px 12px',
        borderBottom: `1px solid ${palette.amber}`,
        background: palette.amberWash,
      }}>
        <AlertTriangle size={14} color={palette.amber} strokeWidth={2.25} />
        <span style={{ fontSize: 11, fontWeight: 700, color: palette.amber, letterSpacing: 0.4, textTransform: 'uppercase' }}>
          Action required
        </span>
      </div>
      {items.map((item, idx) => (
        <Row
          key={`${item.kind}-${idx}`}
          item={item}
          view={viewFor(item)}
          onTap={onItemTap ? () => onItemTap(item) : undefined}
          isLast={idx === items.length - 1}
        />
      ))}
    </div>
  );
}
